import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useComments } from "@/hooks/useComments"
import { useAuthState } from "@/hooks/useAuth"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Loading } from "@/components/ui/loading"
import { AuthModal } from "@/components/auth/AuthModal"
import { Send, MessageCircle, X } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { getUserIcon } from "@/lib/userIcons"

interface FlashCommentsProps {
  flashId: string
}

export function FlashComments({ flashId }: FlashCommentsProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const { 
    comments, 
    loading, 
    error, 
    submitting, 
    deleting, 
    userHasCommented, 
    addComment, 
    deleteComment 
  } = useComments(flashId)
  const [commentText, setCommentText] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!commentText.trim()) return

    const success = await addComment(commentText)
    if (success) {
      setCommentText("")
    }
  }

  const handleDelete = async (commentId: string) => {
    const success = await deleteComment(commentId)
    if (success) {
      // After successful deletion, user can comment again
      setCommentText("")
    }
  }

  const handleAuthSuccess = () => {
    // User is now authenticated, they can try commenting again
  }

  return (
    <div className="rounded-[var(--card-radius)] bg-card border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">
            {t('comments.title', 'Comments')}
          </h3>
          <span className="text-sm text-muted-foreground">
            ({comments.length})
          </span>
        </div>
      </div>

      {/* Comment Input */}
      <div className="p-4 border-b border-border">
        {user ? (
          userHasCommented ? (
            <div className="text-center text-muted-foreground">
              <p className="text-sm mb-2">You've already commented on this flash</p>
              <p className="text-xs">Delete your comment below to post a new one</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex gap-2">
              <Input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('comments.placeholder', 'Add a comment...')}
                className="flex-1 h-12"
                disabled={submitting}
              />
              <Button
                type="submit"
                size="default"
                disabled={!commentText.trim() || submitting}
                className="h-12 w-12 rounded-[var(--radius)]"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          )
        ) : (
          <AuthModal onSuccess={handleAuthSuccess}>
            <div className="flex gap-2">
              <Input
                placeholder={t('comments.loginToComment', 'Login to comment...')}
                className="flex-1 h-12"
                disabled
              />
              <Button size="default" className="h-12 w-12 rounded-[var(--radius)]">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </AuthModal>
        )}
        
        {error && (
          <p className="text-sm text-destructive mt-2">{error}</p>
        )}
      </div>

      {/* Comments List */}
      <div className="max-h-80 overflow-y-auto">
        {loading ? (
          <div className="p-4 flex justify-center">
            <Loading />
          </div>
        ) : comments.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {t('comments.noComments', 'No comments yet. Be the first to comment!')}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {comments.map((comment) => (
              <div key={comment.id} className="p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex gap-3 flex-1">
                    {/* User Animal Icon */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center text-base">
                      {getUserIcon(comment.userId)}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-foreground">
                          {comment.commenter}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(comment.createdAt, { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">
                        {comment.comment}
                      </p>
                    </div>
                  </div>
                  
                  {/* Delete button - only show for user's own comments */}
                  {user && comment.userId === user.uid && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(comment.id)}
                      disabled={deleting === comment.id}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                    >
                      {deleting === comment.id ? (
                        <div className="w-3 h-3 border border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                      ) : (
                        <X className="w-3 h-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
} 